import { MessageCircle, Search, Sparkles, ThumbsUp, Trash2, Undo2 } from "lucide-react";
import { useMemo, useState } from "react";
import type { PlaceStatus } from "@shared/types";
import type { PlaceCategory, PlaceDTO } from "@shared/types";
import { PLACE_CATEGORIES } from "@shared/types";
import { t, tn } from "@/i18n";
import { fmtDayShort } from "@/lib/format";
import { CATEGORY, PLACE_STATUS } from "@/lib/meta";
import { toast, useUI } from "@/lib/store";
import { tripApi, type Bundle, type Derived } from "@/lib/trip";
import { Button, Chip, cx, Empty, Input } from "../ui";

type StatusFilter = "all" | "idea" | "want" | "plan" | "visited" | "social" | "orphan";

export function PlacesPanel({ b, d }: { b: Bundle; d: Derived }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [cat, setCat] = useState<PlaceCategory | null>(null);
  const selectPlace = useUI((s) => s.selectPlace);
  const set = useUI((s) => s.set);

  const groups = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = b.places.filter((p) => {
      const inPlan = (d.placeDays.get(p.id)?.length ?? 0) > 0;
      if (status === "plan" && !inPlan) return false;
      if (status === "idea" && (p.status !== "idea" || inPlan)) return false;
      if (status === "want" && p.status !== "want") return false;
      if (status === "visited" && p.status !== "visited") return false;
      if (status === "social" && !(p.votes?.length || p.commentCount)) return false;
      if (status === "orphan" && !d.orphans.has(p.id)) return false;
      if (cat && p.category !== cat) return false;
      if (s && !`${p.name} ${p.nameLocal ?? ""} ${p.region ?? ""} ${p.description ?? ""} ${p.tags.join(" ")}`.toLowerCase().includes(s)) return false;
      return true;
    });
    // группы по регионам в порядке маршрута (по первому дню)
    const firstDay = (p: PlaceDTO) => d.placeDays.get(p.id)?.slice().sort()[0] ?? "9999";
    const m = new Map<string, PlaceDTO[]>();
    const noRegion = t("Без региона");
    for (const p of list) m.set(p.region ?? noRegion, [...(m.get(p.region ?? noRegion) ?? []), p]);
    return [...m.entries()]
      .map(([region, ps]) => ({ region, places: ps.sort((a, z) => firstDay(a).localeCompare(firstDay(z)) || a.name.localeCompare(z.name)), order: ps.map(firstDay).sort()[0] }))
      .sort((a, z) => a.order.localeCompare(z.order));
  }, [b.places, d, q, status, cat]);

  const total = groups.reduce((s, g) => s + g.places.length, 0);
  const cats = PLACE_CATEGORIES.filter((c) => b.places.some((p) => p.category === c));

  return (
    <div className="pb-6">
      {b.trip.myRole !== "viewer" && (
        <Button variant="soft" className="mb-3 w-full" icon={<Sparkles size={16} />} onClick={() => set({ placeDraft: {} })}>
          {t("Новое место — словами или ссылкой")}
        </Button>
      )}
      <div className="relative mb-2">
        <Search size={16} className="absolute top-3 left-3 text-muted" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tn(b.places.length, "Поиск среди {n} места|Поиск среди {n} мест|Поиск среди {n} мест")} className="pl-9" />
      </div>
      <div className="no-scrollbar -mx-1 mb-2 flex gap-1.5 overflow-x-auto px-1">
        {(
          [
            ["all", t("Все")],
            ["plan", t("В плане")],
            ["want", t("Хотим")],
            ["idea", t("Идеи")],
            ["visited", t("Были")],
            ["social", "👍 " + t("Обсуждаемые")],
            ...(d.orphans.size || status === "orphan" ? [["orphan", "⚠ " + t("Выпали из плана") + " · " + d.orphans.size]] : []),
          ] as [StatusFilter, string][]
        ).map(([key, l]) => (
          <Chip key={key} active={status === key} onClick={() => setStatus(key)}>
            {l}
          </Chip>
        ))}
      </div>
      <div className="no-scrollbar -mx-1 mb-3 flex gap-1.5 overflow-x-auto px-1">
        {cats.map((c) => (
          <Chip key={c} active={cat === c} color={CATEGORY[c].color} onClick={() => setCat(cat === c ? null : c)}>
            {CATEGORY[c].emoji} {CATEGORY[c].label}
          </Chip>
        ))}
      </div>
      {status === "orphan" && total > 0 && <OrphanActions b={b} ids={groups.flatMap((g) => g.places.map((p) => p.id))} onDone={() => setStatus("all")} />}
      {!total && (
        <Empty icon="🔎" title={t("Ничего не найдено")}>
          {t("Поменяйте фильтры или добавьте место через поиск на карте.")}
        </Empty>
      )}
      {groups.map((g) => (
        <section key={g.region} className="mb-3">
          <h3 className="sticky top-0 z-10 -mx-1 bg-[var(--surface-solid)] px-2 py-1 text-[12px] font-extrabold tracking-wider text-muted uppercase">
            {g.region} <span className="font-semibold">· {g.places.length}</span>
          </h3>
          {g.places.map((p) => {
            const days = d.placeDays.get(p.id) ?? [];
            const di = days[0] ? d.dayIndex.get(days.slice().sort()[0]) : null;
            const ups = p.votes?.filter((v) => v.value > 0).length ?? 0;
            return (
              <button
                key={p.id}
                onClick={() => {
                  selectPlace(p.id);
                  useUI.getState().focusMap({ bounds: [[p.lat, p.lng], [p.lat, p.lng]] });
                }}
                onMouseEnter={() => set({ hoverPlaceId: p.id })}
                onMouseLeave={() => set({ hoverPlaceId: null })}
                className="flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left hover:bg-[var(--surface-2)]"
              >
                <span
                  className={cx("grid size-9 shrink-0 place-items-center rounded-full border-2 text-[16px]", !days.length && "border-dashed")}
                  style={{ borderColor: di?.color ?? CATEGORY[p.category].color }}
                >
                  {CATEGORY[p.category].emoji}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] font-semibold">{p.name}</span>
                  <span className="block truncate text-[12px] text-muted">
                    {days.length ? `📅 ${days.sort().map((x) => fmtDayShort(x)).join(", ")}` : PLACE_STATUS[p.status].label}
                    {p.description ? ` · ${p.description}` : ""}
                  </span>
                </span>
                {ups > 0 && (
                  <span className="flex shrink-0 items-center gap-0.5 text-[12px] font-bold text-jade-600 dark:text-jade-300">
                    <ThumbsUp size={12} /> {ups}
                  </span>
                )}
                {(p.commentCount ?? 0) > 0 && (
                  <span className="flex shrink-0 items-center gap-0.5 text-[12px] font-bold text-sky-600 dark:text-sky-300">
                    <MessageCircle size={12} /> {p.commentCount}
                  </span>
                )}
              </button>
            );
          })}
        </section>
      ))}
    </div>
  );
}

/** Массовые действия с местами, выпавшими из плана: одним действием, отменяется целиком */
function OrphanActions({ b, ids, onDone }: { b: Bundle; ids: string[]; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  if (b.trip.myRole === "viewer") return null;
  const run = async (v: { status?: PlaceStatus; delete?: boolean }, done: string) => {
    if (v.delete && !confirm(tn(ids.length, "Удалить {n} место?|Удалить {n} места?|Удалить {n} мест?"))) return;
    setBusy(true);
    try {
      const r = await tripApi.bulkPlaces(b.trip.id, { ids, ...v });
      toast(done, {
        action: { label: t("Вернуть"), run: () => void tripApi.undo(b.trip.id, { batchId: r.batchId }) },
      });
      onDone();
    } catch (e) {
      toast((e as Error).message, { tone: "error" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mb-3 rounded-2xl border border-amber-300/60 bg-amber-50 p-3 text-[13px] dark:border-amber-400/25 dark:bg-amber-400/10">
      <p className="mb-2 text-muted">{t("Эти места отмечены «хотим», но ни один пункт плана их больше не использует.")}</p>
      <div className="flex flex-wrap gap-1.5">
        <Button size="sm" variant="secondary" disabled={busy} icon={<Undo2 size={14} />} onClick={() => run({ status: "idea" }, tn(ids.length, "{n} место вернулось в идеи|{n} места вернулись в идеи|{n} мест вернулись в идеи"))}>
          {t("Вернуть в идеи")}
        </Button>
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => run({ status: "skipped" }, tn(ids.length, "{n} место отмечено «отказались»|{n} места отмечены «отказались»|{n} мест отмечены «отказались»"))}>
          {t("Отметить «отказались»")}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} icon={<Trash2 size={14} />} onClick={() => run({ delete: true }, tn(ids.length, "Удалено {n} место|Удалено {n} места|Удалено {n} мест"))}>
          {t("Удалить")}
        </Button>
      </div>
    </div>
  );
}
